import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, ChevronLeft, ChevronRight, Zap, Truck, Users } from "lucide-react";

const LOGO_URL = "/atul-logo.png";

const VEHICLES = [
  {
    name: "Elite+",
    tagline: "The e-rickshaw built for city runs",
    caption: "Zero-emission mobility, all-day range",
    img: "https://customer-assets.emergentagent.com/job_atul-auto-control/artifacts/znkcrmrz_download%20%281%29%20Elite.png",
    icon: Zap,
  },
  {
    name: "GEMi",
    tagline: "Cargo three-wheeler, rugged and reliable",
    caption: "Move more, spend less. Diesel & CNG variants.",
    img: "https://customer-assets.emergentagent.com/job_atul-auto-control/artifacts/gi9anbkf_download%20%281%29%20Cargo.png",
    icon: Truck,
  },
  {
    name: "Rik Twin",
    tagline: "The passenger auto redefined",
    caption: "Comfort. Safety. Fuel efficiency.",
    img: "https://customer-assets.emergentagent.com/job_atul-auto-control/artifacts/gxvh6twf_download%20%281%29%20RikTwin.png",
    icon: Users,
  },
];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [slide, setSlide] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % VEHICLES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.force_password_change ? "/change-password" : "/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const active = VEHICLES[slide];
  const ActiveIcon = active.icon;

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left showcase panel */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden"
        style={{
          background: "radial-gradient(circle at 20% 30%, #1E3A8A 0%, #0F172A 60%, #020617 100%)",
        }}>
        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }} />
        {/* Orange glow accent */}
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full opacity-40 blur-3xl"
          style={{ background: "radial-gradient(circle, #F97316 0%, transparent 70%)" }} />

        <div className="relative z-10 p-10 xl:p-14 text-white flex flex-col justify-between w-full">
          {/* Top: Brand */}
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center overflow-hidden shadow-lg p-1.5">
              <img src={LOGO_URL} alt="Atul Auto" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading text-xl font-bold leading-tight">M/s India Atul Motors</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-orange-300 mt-0.5">
                Authorised Atul Auto Dealership
              </div>
            </div>
          </div>

          {/* Middle: vehicle carousel */}
          <div className="flex-1 flex flex-col items-center justify-center -mt-6">
            <div className="w-full max-w-lg relative">
              {/* Vehicle image */}
              <div className="relative h-72 xl:h-80">
                {VEHICLES.map((v, i) => (
                  <img
                    key={v.name}
                    src={v.img}
                    alt={v.name}
                    className={`absolute inset-0 w-full h-full object-contain transition-all duration-700 ease-out ${
                      i === slide ? "opacity-100 scale-100" : "opacity-0 scale-95"
                    }`}
                    data-testid={`vehicle-slide-${i}`}
                  />
                ))}
                {/* Reflection */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3/4 h-6 bg-black/40 blur-2xl rounded-full" />
              </div>

              {/* Vehicle name + tagline */}
              <div className="text-center mt-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/20 border border-orange-400/30 text-orange-300 text-xs font-medium uppercase tracking-wider mb-3">
                  <ActiveIcon className="w-3.5 h-3.5" /> {active.name}
                </div>
                <div className="font-heading text-3xl xl:text-4xl font-bold leading-tight" data-testid="hero-vehicle-name">
                  {active.tagline}
                </div>
                <div className="text-slate-300 text-sm mt-2">{active.caption}</div>
              </div>

              {/* Carousel controls */}
              <div className="mt-6 flex items-center justify-center gap-4">
                <button type="button" onClick={() => setSlide((slide - 1 + VEHICLES.length) % VEHICLES.length)}
                  data-testid="carousel-prev"
                  className="w-9 h-9 rounded-full border border-white/20 hover:bg-white/10 flex items-center justify-center transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex gap-2">
                  {VEHICLES.map((_, i) => (
                    <button key={i} type="button" onClick={() => setSlide(i)} data-testid={`carousel-dot-${i}`}
                      className={`transition-all rounded-full ${
                        i === slide ? "w-8 h-2 bg-orange-500" : "w-2 h-2 bg-white/30 hover:bg-white/50"
                      }`} />
                  ))}
                </div>
                <button type="button" onClick={() => setSlide((slide + 1) % VEHICLES.length)}
                  data-testid="carousel-next"
                  className="w-9 h-9 rounded-full border border-white/20 hover:bg-white/10 flex items-center justify-center transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Bottom: dealership footprint */}
          <div className="border-t border-white/10 pt-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="font-heading text-2xl font-bold text-white">5</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">Branches</div>
              </div>
              <div className="border-x border-white/10">
                <div className="font-heading text-2xl font-bold text-white">3+</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">Models</div>
              </div>
              <div>
                <div className="font-heading text-2xl font-bold text-orange-400">24×7</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">Support</div>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 text-center mt-4">
              Purnea HO · Dilip Enterprises · Alok Automobile · Dhamdha · K. Nagar
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="mb-8 flex items-center gap-3">
            <div className="lg:hidden w-14 h-14 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden p-1">
              <img src={LOGO_URL} alt="Atul Auto" className="w-full h-full object-contain" />
            </div>
            <div>
              <div className="font-heading text-3xl font-bold text-slate-900">Welcome back</div>
              <div className="text-sm text-slate-500 mt-1">Sign in to your dealership dashboard</div>
            </div>
          </div>

          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">Email</label>
          <input
            data-testid="login-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-2.5 rounded-md border border-slate-300 focus:border-blue-800 focus:ring-2 focus:ring-blue-800/20 outline-none text-sm mb-4"
            required
          />

          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">Password</label>
          <input
            data-testid="login-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 rounded-md border border-slate-300 focus:border-blue-800 focus:ring-2 focus:ring-blue-800/20 outline-none text-sm mb-4"
            required
          />

          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4" data-testid="login-error">
              {err}
            </div>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full py-2.5 rounded-md bg-brand-blue bg-brand-blue-hover text-white font-medium text-sm flex items-center justify-center gap-2 transition-colors disabled:opacity-60 shadow-sm hover:shadow-md"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Sign in
          </button>

          <div className="text-[11px] text-slate-400 text-center mt-6">
            © {new Date().getFullYear()} M/s India Atul Motors · Purnea, Bihar
          </div>
        </form>
      </div>
    </div>
  );
}
